import React from 'react'
import Button from '../ui/Button'
import type { CodeSheetDiffCounts } from '../../utils/codeSheetDiff'

interface CodeSheetDiffBarProps {
  counts: CodeSheetDiffCounts
  diffVisible: boolean
  changedOnly: boolean
  loading: boolean
  error?: string | null
  onChangedOnlyChange: (value: boolean) => void
  onRetry: () => void
}

function CountBadge({ label, count, tone }: { label: string; count: number; tone: 'added' | 'modified' | 'deleted' | 'arrived' }) {
  const tones = {
    added: 'bg-[#EAF7EE] text-[#1E7A3F]',
    modified: 'bg-[#FFF4D6] text-[#8A6100]',
    deleted: 'bg-[#FDECEC] text-[#B3261E]',
    arrived: 'border border-[#9CB6CC] bg-white text-[#315B78]',
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone]}`}>
      {count} {label}
    </span>
  )
}

export default function CodeSheetDiffBar({
  counts,
  diffVisible,
  changedOnly,
  loading,
  error,
  onChangedOnlyChange,
  onRetry,
}: CodeSheetDiffBarProps) {
  return (
    <div className="mb-3 flex min-h-[42px] flex-wrap items-center gap-2 rounded-md border border-[#D7E3EF] bg-[#F8FBFF] px-3 py-2" data-testid="codesheet-diff-bar">
      <span className="mr-1 text-sm font-semibold text-gray-700">Project changes</span>
      {loading ? (
        <span className="text-xs text-gray-500">Loading differences…</span>
      ) : error ? (
        <>
          <span className="text-xs font-medium text-[#B3261E]">Diff unavailable.</span>
          <Button size="sm" variant="secondary" onClick={onRetry}>Retry</Button>
        </>
      ) : (
        <>
          <CountBadge label="Added" count={counts.added} tone="added" />
          <CountBadge label="Modified" count={counts.modified} tone="modified" />
          <CountBadge label="Deleted" count={counts.deleted} tone="deleted" />
          {counts.arrived > 0 && (
            <span className="ml-1 border-l border-[#B8C9D8] pl-3">
              <CountBadge label="from later revisions" count={counts.arrived} tone="arrived" />
            </span>
          )}
          <label className={`ml-auto inline-flex items-center gap-2 text-sm ${diffVisible ? 'cursor-pointer text-gray-700' : 'cursor-not-allowed text-gray-400'}`}>
            <input
              type="checkbox"
              checked={changedOnly}
              disabled={!diffVisible}
              onChange={(event) => onChangedOnlyChange(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 accent-[#1D5176]"
            />
            Changed rows only
          </label>
        </>
      )}
    </div>
  )
}
